/** Append-only audit trail (§4.2.8). */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Skeleton,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

interface AuditRow {
  id: string;
  action: string;
  entity: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  createdAt: string;
  user: { name: string; email: string } | null;
}

export function AuditLogPage(): JSX.Element {
  const [filters, setFilters] = useState({ action: '', entity: '', from: '', to: '' });
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['audit', filters, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: '50' });
      if (filters.action) params.set('action', filters.action);
      if (filters.entity) params.set('entity', filters.entity);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      return api<{ data: AuditRow[]; total: number; pageSize: number }>(
        `/settings/audit?${params.toString()}`,
      );
    },
  });

  const total = query.data?.total ?? 0;
  const pageSize = query.data?.pageSize ?? 50;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header>
        <h1 className="text-xl font-bold text-navy dark:text-white">Audit log</h1>
        <p className="text-sm text-slate-500">
          Every change, who made it and when. Entries can never be edited or removed.
        </p>
      </header>

      <Card>
        <div className="mb-4 grid gap-3 sm:grid-cols-4">
          <Field label="Action">
            <input
              className={inputClass}
              placeholder="e.g. LOGIN"
              value={filters.action}
              onChange={(event) => {
                setPage(1);
                setFilters({ ...filters, action: event.target.value });
              }}
            />
          </Field>
          <Field label="Record type">
            <input
              className={inputClass}
              placeholder="e.g. user"
              value={filters.entity}
              onChange={(event) => {
                setPage(1);
                setFilters({ ...filters, entity: event.target.value });
              }}
            />
          </Field>
          <Field label="From">
            <input
              className={inputClass}
              type="date"
              value={filters.from}
              onChange={(event) => {
                setPage(1);
                setFilters({ ...filters, from: event.target.value });
              }}
            />
          </Field>
          <Field label="To">
            <input
              className={inputClass}
              type="date"
              value={filters.to}
              onChange={(event) => {
                setPage(1);
                setFilters({ ...filters, to: event.target.value });
              }}
            />
          </Field>
        </div>

        {query.isLoading && <Skeleton className="h-64 w-full" />}
        {query.data?.data.length === 0 && <EmptyState title="Nothing recorded for those filters." />}

        {(query.data?.data.length ?? 0) > 0 && (
          <>
            <TableWrap>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Who</Th>
                  <Th>Action</Th>
                  <Th>Record</Th>
                  <Th align="right">Detail</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {query.data?.data.map((row) => (
                  <>
                    <tr key={row.id}>
                      <Td>{dateTime(row.createdAt)}</Td>
                      <Td>
                        <p className="font-medium">{row.user?.name ?? 'System'}</p>
                        <p className="text-xs text-slate-500">{row.ip ?? ''}</p>
                      </Td>
                      <Td className="font-mono text-xs">{row.action}</Td>
                      <Td>
                        {row.entity}
                        {row.entityId && (
                          <span className="block font-mono text-xs text-slate-400">
                            {row.entityId.slice(0, 8)}…
                          </span>
                        )}
                      </Td>
                      <Td align="right">
                        {Boolean(row.before ?? row.after) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                          >
                            {expanded === row.id ? 'Hide' : 'Show'}
                          </Button>
                        )}
                      </Td>
                    </tr>
                    {expanded === row.id && (
                      <tr key={`${row.id}-detail`}>
                        <td colSpan={5} className="bg-slate-50 px-3 py-3 dark:bg-slate-800">
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <p className="mb-1 text-xs font-semibold uppercase text-slate-500">
                                Before
                              </p>
                              <pre className="overflow-x-auto rounded-lg bg-white p-2 text-xs dark:bg-slate-900">
                                {JSON.stringify(row.before, null, 2) || '—'}
                              </pre>
                            </div>
                            <div>
                              <p className="mb-1 text-xs font-semibold uppercase text-slate-500">
                                After
                              </p>
                              <pre className="overflow-x-auto rounded-lg bg-white p-2 text-xs dark:bg-slate-900">
                                {JSON.stringify(row.after, null, 2) || '—'}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </TableWrap>

            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span>
                {total} entr{total === 1 ? 'y' : 'ies'} · page {page} of {lastPage}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((value) => value - 1)}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page >= lastPage}
                  onClick={() => setPage((value) => value + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
